import { useState, useCallback, useEffect } from "react";
import SectionContainer from "../../components/UI/SectionContainer";
import UserSearchBox from "../../components/UI/UserSearchBox";
import Pagination from "../../components/UI/Pagination";
import { openAlert, USER_STATE, exportExcel } from '../../utils/common';
import Spinner from '../../components/UI/Spinner';
import userService from '../../services/userService';

// 탈퇴 사유 번역 함수 (모바일 앱 번역과 일치)
const translateSignoutReason = (reason) => {
  const reasonMap = {
    'not_useful': '서비스를 잘 이용하지  않음',
    'technical_issue': '서비스 이용이 복잡하고 어려움',
    'service_disruption': '서비스 장애와 오류 때문에',
    'temporary_break': '탈퇴 후 신규 가입하기 위함',
    'privacy_concern': '개인정보 및 보안 우려',
    'other': '기타'
  };
  
  return reasonMap[reason] || reason || '-';
};

const SecessionListPage = () => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchKind, setSearchKind] = useState('0');
    const [keyword, setKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const pageSize = 10;
    const pageBlock = 5;

    const fetchList = useCallback(async (page = 1, skArg, kwArg) => {
      setLoading(true);
      try {
        const sk = skArg !== undefined ? Number(skArg) : 0;
        const kwState = kwArg !== undefined ? kwArg : '';
        
        const response = await userService.getUserList(
          page,
          pageSize,
          sk,
          kwState,
          USER_STATE.SECESSION
        );
        
        if (response.success) {
          setList(response.data.users);
          setTotalCount(response.data.pagination.totalCount);
        } else {
          openAlert({ title: '알림', description: response.message });
        }
      } catch (error) { 
        console.error('fetchList error:', error); 
        openAlert({ title: '알림', description: error.message || '데이터 로드 중 오류가 발생했습니다.' });
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchList(currentPage, searchKind, keyword);
    }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (directKeyword) => { 
      setCurrentPage(1); 
      // Use directKeyword if provided (from suggestion select), otherwise use keyword from state
      fetchList(1, searchKind, directKeyword !== undefined ? directKeyword : keyword); 
    };
    const handleReset = () => { 
      setSearchKind('0'); 
      setKeyword(''); 
      setCurrentPage(1); 
      // Call fetchList directly with reset values, consistent with new handleSearch signature
      fetchList(1, '0', ''); 
    };

    const handleExcelDownload = async () => {
        setLoading(true);
        try {
            const response = await userService.getUserExcelData(
                Number(searchKind),
                keyword,
                USER_STATE.SECESSION
            );
            
            if (response.success) {
                const excelConfig = {
                    excelFileName : "탈퇴회원목록.xlsx",
                    sheetName : "Sheet1"
                }
                exportExcel(response.data, excelConfig);
            } else {
                openAlert({ title: '알림', description: response.message });
            }
        } catch (error) {
            console.error('handleExcelDownload error:', error);
            openAlert({ title: '알림', description: error.message || '엑셀 다운로드 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    }

    return (
      <SectionContainer title="탈퇴회원 관리">
        <UserSearchBox
          value={keyword}
          userState={USER_STATE.SECESSION}
          onChange={setKeyword}
          searchKind={searchKind}
          onSelect={setSearchKind}
          onSearch={handleSearch}
          onReset={handleReset}
          onSuggestSelect={(selectedVal) => {
            setKeyword(selectedVal); // Update the keyword state
            handleSearch(selectedVal); // And immediately search with this selected value
          }}
          totalCount={totalCount}
          onExcelDownload={handleExcelDownload}
        />
        <div className="content_area">
          <Spinner visible={loading} />
          <div className="table_area">
            <div className="table">
              <table>
                <thead>
                  <tr>
                    <th>성명</th>
                    <th>아이디</th>
                    <th>이메일</th>
                    <th>가입일</th>
                    <th>탈퇴일</th>
                    <th>탈퇴사유</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && list.length === 0 ? (
                    <tr>
                      <td colSpan="6">회원 내역이 존재하지 않습니다.</td>
                    </tr>
                  ) : (
                    list.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.userName}</td>
                        <td>{item.userId}</td>
                        <td>{item.userEmail}</td>
                        <td>{item.userRegDate}</td>
                        <td>{item.outDate}</td>
                        <td>{translateSignoutReason(item.outReason)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <Pagination
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          pageBlock={pageBlock}
          onPageChange={setCurrentPage}
        />
      </SectionContainer>
    );
}

export default SecessionListPage; 